import React, { useState, useEffect } from 'react';
import { Award, Download, Calendar, ExternalLink, FileText, Trophy } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const MeusCertificados = () => {
  const [certificados, setCertificados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    buscarCertificados();
  }, []);

  const buscarCertificados = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/certificados/meus', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setCertificados(data.certificados || []);
      } else {
        setError(data.erro || 'Erro ao carregar certificados');
      }
    } catch (error) {
      console.error('Erro:', error);
      setError('Erro ao carregar certificados');
    } finally {
      setLoading(false);
    }
  };

  const abrirCertificado = (pdfUrl) => {
    window.open(`http://localhost:5000${pdfUrl}`, '_blank');
  };

  const copiarCredencial = (credentialId) => {
    navigator.clipboard.writeText(credentialId);
    // Você pode adicionar um toast aqui para feedback
    alert('Credencial copiada para a área de transferência!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-red-500/30 border-t-red-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400">Carregando certificados...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full mb-6 shadow-2xl">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Meus Certificados
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Aqui estão todos os certificados dos cursos que você concluiu com sucesso.
          </p>
        </div>

        {/* Erro */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-8">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Lista de Certificados */}
        {certificados.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-12 h-12 text-slate-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              Nenhum certificado ainda
            </h3>
            <p className="text-slate-400 text-lg mb-8">
              Complete seus cursos para começar a colecionar certificados!
            </p>
            <Button 
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white"
              onClick={() => window.location.href = '/meus-cursos'}
            >
              <FileText className="w-4 h-4 mr-2" />
              Ver Meus Cursos
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificados.map((certificado) => (
              <Card 
                key={certificado.id} 
                className="bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 border border-slate-700/40 hover:border-yellow-500/50 transition-all duration-300 overflow-hidden group hover:shadow-2xl hover:shadow-yellow-500/10"
              >
                <CardContent className="p-0">
                  {/* Header do certificado */}
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 p-6 text-center relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/20 to-yellow-600/20 backdrop-blur-sm"></div>
                    <div className="relative">
                      <Award className="w-12 h-12 text-white mx-auto mb-3" />
                      <h3 className="font-bold text-white text-lg mb-1">
                        Certificado
                      </h3>
                      <Badge className="bg-white/20 text-white border-0 text-xs">
                        Concluído
                      </Badge>
                    </div>
                  </div>

                  {/* Conteúdo */}
                  <div className="p-6 space-y-4">
                    <div>
                      <h4 className="font-bold text-white text-lg mb-2 line-clamp-2">
                        {certificado.nome_curso}
                      </h4>
                      
                      <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>Concluído em {certificado.data_conclusao}</span>
                      </div>

                      <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-slate-400 mb-1">Credencial ID:</p>
                        <p 
                          className="text-sm text-yellow-400 font-mono cursor-pointer hover:text-yellow-300 transition-colors"
                          onClick={() => copiarCredencial(certificado.credential_id)}
                          title="Clique para copiar"
                        >
                          {certificado.credential_id.substring(0, 8)}...
                        </p>
                      </div>
                    </div>

                    {/* Ações */}
                    <div className="space-y-2">
                      <Button 
                        onClick={() => abrirCertificado(certificado.pdf_url)}
                        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Ver Certificado
                      </Button>
                      
                      <Button 
                        variant="outline"
                        onClick={() => window.open(`/certificados/${certificado.credential_id}`, '_blank')}
                        className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Validar Online
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeusCertificados; 